import Vue from 'vue';
import { isEqual, cloneDeep } from 'lodash-es';
import cookies from 'js-cookie';

const plans = [
  {
    name: 'the-complete-365-data-science-program-monthly-subscription',
    interval: 'month',
    intervalLength: 1,
    title: 'Monthly Plan',
    shortTitle: 'Monthly',
    billText: 'Billed monthly',
    getButtonText: 'Get Monthly',
    getButtonTextSite: 'Get Monthly',
    price: 0,
    id: 0,
    subscription: true,
    slug: 'monthly',
    currency: { },
  },
  {
    name: 'the-complete-365-data-science-program-quarterly-subscription',
    interval: 'month',
    intervalLength: 3,
    title: 'Quarterly Plan',
    shortTitle: 'Quarterly',
    billText: 'Billed quarterly',
    getButtonText: 'Get and Save 36%',
    getButtonTextSite: 'Get and Save 36%',
    getButtonTextPlaceholder: 'Get Quarterly',
    price: 0,
    id: 1,
    subscription: true,
    slug: 'quarterly',
    currency: { },
  },
  {
    name: 'the-complete-365-data-science-program-annual-subscription',
    interval: 'year',
    title: 'Annual Plan',
    shortTitle: 'Annual',
    billText: 'Billed annually',
    getButtonText: 'Get and Save 47%',
    getButtonTextSite: 'Get and Save 20%',
    getButtonTextPlaceholder: 'Get Annual',
    price: 0,
    id: 2,
    subscription: true,
    bestOffer: true,
    slug: 'annual',
    currency: { },
  },
  {
    name: 'the-365-data-science-program-lifetime-plan',
    interval: 'lifetime',
    title: 'Lifetime Plan',
    shortTitle: 'Lifetime',
    slug: 'lifetime',
    price: 0,
    id: 3,
    currency: { },
  },
  {}, {}, {}, {}, {}, {},
  {
    name: 'the-complete-365-data-science-program-business-subscription',
    interval: 'year',
    title: 'Business Plan',
    shortTitle: 'Business',
    billText: 'Billed annually',
    getButtonText: 'Get and Save 47%',
    getButtonTextSite: 'Get and Save 20%',
    getButtonTextPlaceholder: 'Get Annual',
    price: 49,
    id: 4,
    subscription: true,
    bestOffer: true,
    slug: 'business',
    currency: { sign: '$', value: 'USD' },
  },
  { name: '365-data-science-german-monthly-subscription', interval: 'month', addon: true },
  { name: '365-data-science-german-annual-subscription', interval: 'year', addon: true },
];

const upgradeSuggestions = {
  free: [0, 1, 2],
  0: [1, 2],
  1: [2],
};

const typeToSource = {
  collection: 'benefit',
  referral: 'referral',
  loyalty: 'platform',
};

const formatSubscriptionDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }) : null);

export default {
  namespaced: true,
  state: () => ({
    plans: cloneDeep(plans),
    active: {},
    discounts: [],
    banner: null,
    saving: 0,
    hideBanner: cookies.get('hideBanners'),
    currency: { sign: '$', value: 'USD' },
    exchangeRate: 1,
  }),

  getters: {
    active(state, getters, { auth: { user = {} } }) {
      const { active = {} } = state;

      let plan;
      if (active.id) {
        plan = state.plans[active.plan];
      } else if (user && (user.activeSubscriptionPlan || user.activeSubscriptionPlan === 0)) {
        plan = state.plans[user.activeSubscriptionPlan];
      } else {
        plan = {
          name: 'free',
          interval: 'free',
          title: 'Free plan',
          shortTitle: 'Free',
        };
      }

      plan.free = plan.name === 'free';
      plan.price = plan.price || 0;
      plan.isMonthly = plan.id === getters.monthly.id;
      plan.isQuarterly = plan.id === getters.quarterly.id;
      plan.isAnnual = plan.id === getters.annual.id;

      return {
        ...active,
        periodStart: formatSubscriptionDate(active.periodStart),
        nextCharge: formatSubscriptionDate(active.nextCharge),
        end: formatSubscriptionDate(active.end),
        currency: active.currency || '$',
        provider: user ? user.activeSubscriptionProvider : null,
        plan,
      };
    },

    next(state, getters, { campaign, auth: { user = {} }, banners }) {
      if (!user) {
        return null;
      }

      let currentPlan = 'free';
      if (state.active.id) {
        currentPlan = state.active.plan;
      } else if (user.activeSubscriptionPlan || user.activeSubscriptionPlan === 0) {
        currentPlan = user.activeSubscriptionPlan;
      }

      const suggestions = upgradeSuggestions[currentPlan];
      let suggestedPlan;
      let discountedPlan = {};
      const { monthly, quarterly, annual } = getters;

      if (monthly.discountPrice < monthly.price) {
        discountedPlan = monthly;
      }
      if (quarterly.discountPrice < quarterly.price) {
        discountedPlan = quarterly;
      }
      if (annual.discountPrice < annual.price) {
        discountedPlan = annual;
      }

      const offer = state.plans.find((p) => p.source) || {};
      const banner = banners.alternativeActive || banners.active || {};

      if (((offer && offer.id) || ((banner.prices) && !banner.free)) && (currentPlan === 'free' || discountedPlan.id > currentPlan)) {
        suggestedPlan = offer.id || (campaign !== 'cyber-monday-lifetime-2024' && banner.plan) || discountedPlan.id;

        if (!suggestedPlan && banner.typeName === 'promo') {
          suggestedPlan = 2;
        }
      } else if (suggestions) {
        ([suggestedPlan] = suggestions);
      }

      return suggestions ? suggestedPlan : false;
    },

    nextPlan(state, getters, { banners }) {
      const nextPlan = cloneDeep(state.plans[getters.next]);
      const banner = banners.alternativeActive || banners.active || {};

      if (getters.next && banner && (banner.prices && !banner.free) && banner.active && banner.plan === nextPlan.id) {
        nextPlan.discount = banner.discount;
        nextPlan.discountPrice = banner.discountPrice;
        nextPlan.coupon = banner.coupon;
      }

      return nextPlan;
    },

    all(state) {
      return state.plans.filter((s) => s.subscription).reverse();
    },

    monthly(state, getters, { banners }) {
      const plan = { ...state.plans[0] };

      if (banners.active && banners.active.prices && banners.active.active && banners.active.plan === 0) {
        plan.discount = banners.active.discount;
        plan.discountPrice = banners.active.discountPrice;
        plan.coupon = banners.active.coupon;
        plan.source = true;
      }

      return plan;
    },

    quarterly(state, getters, { banners }) {
      const plan = { ...state.plans[1] };

      if (banners.active && banners.active.prices && banners.active.active && banners.active.plan === 1) {
        plan.discount = banners.active.discount;
        plan.discountPrice = banners.active.discountPrice;
        plan.coupon = banners.active.coupon;
        plan.source = true;
      }

      return plan;
    },

    annual(state, getters, { banners }) {
      const plan = { ...state.plans[2] };

      if (banners.active && banners.active.prices && banners.active.active && banners.active.plan === 2) {
        plan.discount = banners.active.discount;
        plan.discountPrice = banners.active.discountPrice;
        plan.coupon = banners.active.coupon;
        plan.source = typeToSource[banners.active.typeName];
      }

      return plan;
    },
    lifetime(state) {
      return state.plans[3];
    },
    business(state) {
      return state.plans[10];
    },
  },

  mutations: {
    setPlanPrice(state, {
      plan, extId, price, discountPrice, currency, expiration, source,
    }) {
      Vue.set(state.plans[plan], 'extId', extId);
      Vue.set(state.plans[plan], 'price', price);
      Vue.set(state.plans[plan], 'discountPrice', discountPrice);
      Vue.set(state.plans[plan], 'currency', currency);
      Vue.set(state.plans[plan], 'expiration', expiration);
      Vue.set(state.plans[plan], 'source', source);
    },

    setActive(state, subscription) {
      state.active = subscription || {};
    },

    setCanceled(state) {
      Vue.set(state.active, 'canceled', new Date());
    },

    setSaving(state, payload) {
      state.saving = payload;
    },

    setCurrency(state, currency) {
      state.currency = currency;
    },
    setExchangeRate(state, exchangeRate) {
      state.exchangeRate = exchangeRate;
    },
  },

  actions: {
    async fetchPrices({ commit, getters }, force = false) {
      if (!getters.monthly.price || !getters.quarterly.price || !getters.annual.price || force) {
        const { plans: prices, currency, exchangeRate } = await this.$axios.$get('payments/prices', { progress: false });
        commit('setCurrency', currency);
        commit('setExchangeRate', exchangeRate);
        prices.map((p) => commit('setPlanPrice', p));
      }
    },

    async checkout({ getters, dispatch }, { plan, coupon } = {}) {
      if (!getters.active.plan.free) {
        const confirm = await dispatch('modals/open', {
          name: 'confirm',
          title: 'Upgrade plan?',
          message: `Once you upgrade to a quarterly or annual plan, your renewal date will be extended for the respective period
                    from the initial order date of your original subscription. You will immediately be refunded or charged the difference based on the cost of 
                    the unused time from the month compared to the cost of the new plan.`,
          confirm: 'Upgrade',
          cancel: 'Close',
        }, { root: true });

        if (!confirm) {
          return false;
        }

        try {
          await this.$axios.$patch('/payments/subscriptions', { newProduct: plan || getters.nextPlan.name, coupon });

          this.$router.push({ name: 'thank-you', params: { title: "Congrats! You've successfully upgraded your subscription." } });
          // dispatch('getUserSubscriptions');
        } catch (e) {
          return false;
        }
      }

      return true;
    },

    async getUserSubscriptions({
      state, commit, rootState, dispatch,
    }) {
      const response = await this.$axios.$get('/payments/subscriptions', { progress: false }).catch(() => ({}));

      if (response) {
        const { subscription, paidAccess } = response;

        if (!isEqual(subscription, state.active)) {
          commit('setActive', subscription);
        }

        const activeSubscriptionPlan = subscription ? subscription.plan : null;

        if (rootState.auth.user.activeSubscription !== !!paidAccess) {
          // eslint-disable-next-line no-unused-expressions
          await dispatch('auth/refreshAccessToken', null, { root: true });
          commit('auth/setActiveSubscription', !!paidAccess, { root: true });
        }

        if (rootState.auth.user.activeSubscriptionPlan !== activeSubscriptionPlan) {
          commit('auth/setActiveSubscriptionPlan', activeSubscriptionPlan, { root: true });
        }
      }
    },
  },
};
